import CustomTooltip from '../../../shared/CustomTooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import Close from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Alert,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    InputLabel,
    Stack,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useApplyDiscountToBookingMutation } from '../../../../app/Slices/SevenDayApiSlice';
import FormTextField from '../../../shared/FormTextField';
import toast from '../../../shared/Toast';

const DiscountModal = ({
    open,
    onClose,
    reservation,
}: {
    open: boolean;
    onClose: () => void;
    reservation: any;
}) => {
    const [applyDiscount] = useApplyDiscountToBookingMutation();
    const [cancel, setCancel] = useState<boolean>(false);
    const [confirmed, setConfirmed] = useState<boolean>(false);
    const [removeConfirmed, setRemoveConfirmed] = useState<boolean>(false);
    const Schema = z.object({
        percentage: z.coerce
            .number()
            .min(1, { message: 'Discount percentage must be greater than 0' })
            .max(100, { message: 'Discount percentage must be less than 100' }),
    });

    type Discount = z.infer<typeof Schema>;

    const getDiscountPercentage = (array: number[]): number | null =>
        array?.reduce<number | null>((acc, curr, i) => {
            if (i === 0) return curr;
            return acc === curr ? acc : null;
        }, null);

    const currentDiscountPercentage = getDiscountPercentage(
        reservation?.charges
            .filter((charge: any) => charge.chargeType === 'price')
            .map((charge: any) => charge.discount.percentage),
    );

    const methods = useForm<Discount>({
        defaultValues: {
            percentage: currentDiscountPercentage || 0,
        },
        resolver: zodResolver(Schema),
        shouldUnregister: true,
    });

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty },
    } = methods;

    useEffect(() => {
        if (open) {
            reset({ percentage: currentDiscountPercentage || 0 });
        }
    }, [open, currentDiscountPercentage, reset]);

    const handleClose = () => {
        if (isDirty) {
            if (!cancel) {
                setCancel(true);
                setRemoveConfirmed(false);
                setConfirmed(false);
                return;
            }
        }

        if (cancel) reset();

        setCancel(false);
        setRemoveConfirmed(false);
        setConfirmed(false);
        onClose();
    };

    const onSubmit = async (data: Discount) => {
        if (!confirmed) {
            setConfirmed(true);
            setRemoveConfirmed(false);
            setCancel(false);
            return;
        }
        try {
            await applyDiscount([reservation?.id, data]).unwrap();

            onClose();
            setConfirmed(false);
            setCancel(false);
            toast.success('Discount applied successfully');
        } catch (error) {
            toast.error('Error applying discount');
        }
    };
    const onRemoveDiscount = async () => {
        if (!removeConfirmed) {
            setRemoveConfirmed(true);
            setConfirmed(false);
            setCancel(false);
            return;
        }
        try {
            const data = {
                percentage: 0,
            };
            await applyDiscount([reservation?.id, data]).unwrap();

            onClose();
            setRemoveConfirmed(false);
            setConfirmed(false);
            setCancel(false);
            toast.success('Discount applied successfully');
        } catch (error) {
            toast.error('Error applying discount');
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>
                        Apply Discount
                        <IconButton
                            onClick={onClose}
                            sx={{ color: 'text.primary' }}
                            aria-label='close'
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        {currentDiscountPercentage === null && (
                            <Alert severity='info' sx={{ mb: 2 }}>
                                Because multiple promotions overlap within the
                                booking period, various discounts are currently
                                applied to different periods of the booking.
                            </Alert>
                        )}
                        <Stack spacing={1} sx={{ mb: 1 }}>
                            <InputLabel>
                                Discount Percentage{' '}
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            This will overwrite any existing
                                            discounts on the booking. Discounts
                                            apply only to the base price.
                                            Cleaning fees and other additional
                                            fees are not discounted.
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: (theme) =>
                                                theme.palette.text.primary,
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </InputLabel>

                            <FormTextField
                                name='percentage'
                                type='number'
                                control={control}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <Typography>%</Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Stack>
                        <Collapse in={cancel}>
                            <Alert severity='warning'>
                                You have unsaved changes. Are you sure you want
                                to cancel?
                            </Alert>
                        </Collapse>
                        <Collapse in={removeConfirmed}>
                            <Alert severity='info'>
                                This will remove the discount from the booking.
                                Click 'Remove Discount' to confirm.
                            </Alert>
                        </Collapse>
                        <Collapse in={confirmed}>
                            <Alert severity='info'>
                                This will overwrite any existing discounts
                                applied to this booking. Click 'Apply Discount'
                                to confirm.
                            </Alert>
                        </Collapse>
                    </DialogContent>
                    <DialogActions>
                        <Button variant='outlined' onClick={onClose}>
                            Cancel
                        </Button>
                        {reservation?.bookingTotal?.discount > 0 && (
                            <Button
                                variant='contained'
                                color='error'
                                type='button'
                                onClick={onRemoveDiscount}
                            >
                                Remove Discount
                            </Button>
                        )}
                        <Button variant='contained' type='submit'>
                            Apply Discount
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default DiscountModal;
